import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TrustScoreCard from './TrustScoreCard';
import PixelGradeBadge from './PixelGradeBadge';
import GradeProgressBar from './GradeProgressBar';
import { scoreToGrade } from '../utils/GradeUtils';
import { STATUS } from '../utils/StatusLanguage';
import CriticalPathCard from './CriticalPathCard';
import OrgMosaicGrid from './OrgMosaicGrid';
import VerifyRemediation from './VerifyRemediation';
import * as api from '../services/api';
import { connectSocket, disconnectSocket, onEvent } from '../services/socket';
import { apiConfig } from '../config/authConfig';

// No fallback mock data — dashboard shows error state when API is unavailable

interface DashboardData {
  tenant: { name: string; subscription: string };
  clients: Array<{ clientId: string; clientName: string; avgScore: number; grade: string; agentCount: number; onlineCount: number; industry?: string }>;
  fleetStats: { totalAgents: number; online: number; offline: number; error: number; maintenance: number };
  avgScore: number;
  alerts: Array<{ id: string; client: string; type: string; severity: string; message: string; timestamp: string; status: string }>;
}

// Fleet Overview Card
const FleetOverviewCard: React.FC<{ stats: DashboardData['fleetStats']; score: number }> = ({ stats, score }) => (
  <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-6">
    <div className="flex items-center justify-between mb-6">
      <h3 className="text-lg font-medium text-white">{STATUS.FLEET_OVERVIEW}</h3>
      <span className="text-xs text-anchor-muted">
        Last updated: {new Date().toLocaleTimeString()}
      </span>
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      <div className="text-center p-3 bg-white/[0.02] rounded-lg">
        <div className="text-3xl font-bold text-anchor-blue">{stats.totalAgents}</div>
        <div className="text-xs text-anchor-muted mt-1">Total Agents</div>
      </div>
      <div className="text-center p-3 bg-white/[0.02] rounded-lg">
        <div className="text-3xl font-bold text-green-400">{stats.online}</div>
        <div className="text-xs text-anchor-muted mt-1">Online</div>
      </div>
      <div className="text-center p-3 bg-white/[0.02] rounded-lg">
        <div className="text-3xl font-bold text-anchor-muted">{stats.offline}</div>
        <div className="text-xs text-anchor-muted mt-1">Offline</div>
      </div>
      <div className="text-center p-3 bg-white/[0.02] rounded-lg">
        <div className="text-3xl font-bold text-red-400">{stats.error}</div>
        <div className="text-xs text-anchor-muted mt-1">Errors</div>
      </div>
      <div className="text-center p-3 bg-white/[0.02] rounded-lg">
        <div className="text-3xl font-bold text-yellow-400">{stats.maintenance}</div>
        <div className="text-xs text-anchor-muted mt-1">Maintenance</div>
      </div>
    </div>
    <div className="mt-6">
      <GradeProgressBar score={score} />
    </div>
  </div>
);

// Clients Table
const ClientsTable: React.FC<{ clients: DashboardData['clients']; onClientClick?: (clientId: string) => void }> = ({ clients, onClientClick }) => (
  <div className="bg-anchor-card border border-white/[0.06] rounded-xl overflow-hidden">
    <div className="px-6 py-4 border-b border-white/[0.06]">
      <h3 className="text-lg font-medium text-white">Client Organizations</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-white/[0.06]">
        <thead>
          <tr className="bg-white/[0.02]">
            <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Organization</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Agents</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Trust Score</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-anchor-muted uppercase tracking-wider">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.04]">
          {clients.map((client) => {
            const { grade, color } = scoreToGrade(client.avgScore);
            return (
              <tr key={client.clientId} onClick={() => onClientClick?.(client.clientId)} className="hover:bg-white/[0.02] cursor-pointer transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-anchor-blue/20 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-anchor-blue">{client.clientName.charAt(0)}</span>
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-white">{client.clientName}</div>
                      {client.industry && <div className="text-xs text-anchor-muted">{client.industry}</div>}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-white/70">{client.onlineCount}/{client.agentCount}</span>
                  <span className="text-xs text-anchor-muted ml-1">online</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <PixelGradeBadge score={client.avgScore} size={24} />
                    <span className="text-sm font-medium" style={{ color }}>{grade}</span>
                    <span className="text-sm text-white/50">{client.avgScore}%</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    client.avgScore >= 90 ? 'bg-green-400/10 text-green-400' :
                    client.avgScore >= 75 ? 'bg-yellow-400/10 text-yellow-400' :
                    'bg-red-400/10 text-red-400'
                  }`}>
                    {client.avgScore >= 90 ? 'Anchor Set' : client.avgScore >= 75 ? 'Anchor Drifting' : 'Anchor Dragging'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

// Inline spinner for button loading states
const ButtonSpinner: React.FC = () => (
  <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

// Toast notification for quick action feedback
const ActionToast: React.FC<{ message: string; type: 'success' | 'error' | 'info'; onDismiss: () => void }> = ({ message, type, onDismiss }) => {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);

  const colors = {
    success: 'bg-green-500/10 border-green-500/20 text-green-400',
    error: 'bg-red-500/10 border-red-500/20 text-red-400',
    info: 'bg-anchor-blue/10 border-anchor-blue/20 text-anchor-blue',
  };

  return (
    <div className={`mt-3 text-xs border rounded-lg px-3 py-2 flex items-center justify-between ${colors[type]}`}>
      <span>{message}</span>
      <button onClick={onDismiss} className="ml-2 opacity-60 hover:opacity-100">&times;</button>
    </div>
  );
};

// Add Client Modal
const AddClientModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, contactEmail: string) => Promise<void>;
  isSubmitting: boolean;
}> = ({ isOpen, onClose, onSubmit, isSubmitting }) => {
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(name, contactEmail);
    setName('');
    setContactEmail('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-anchor-card border border-white/[0.08] rounded-xl p-6 w-full max-w-md mx-4 shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-medium text-white">Add Client</h3>
          <button onClick={onClose} className="text-anchor-muted hover:text-white transition-colors text-xl leading-none">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="client-name" className="block text-xs font-medium text-anchor-muted mb-1.5">
              Client Name <span className="text-red-400">*</span>
            </label>
            <input
              id="client-name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Contoso Ltd"
              className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-anchor-blue/50 focus:ring-1 focus:ring-anchor-blue/30 transition-colors"
              disabled={isSubmitting}
            />
          </div>
          <div>
            <label htmlFor="client-email" className="block text-xs font-medium text-anchor-muted mb-1.5">
              Contact Email <span className="text-anchor-muted">(optional)</span>
            </label>
            <input
              id="client-email"
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              placeholder="admin@contoso.com"
              className="w-full px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-sm text-white placeholder-white/30 focus:outline-none focus:border-anchor-blue/50 focus:ring-1 focus:ring-anchor-blue/30 transition-colors"
              disabled={isSubmitting}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 border border-white/[0.08] rounded-lg text-sm font-medium text-anchor-light bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim()}
              className="flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-anchor-blue hover:bg-anchor-blue/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting && <ButtonSpinner />}
              {isSubmitting ? 'Creating...' : 'Create Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Deploy Updates Confirmation Dialog
const DeployConfirmDialog: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}> = ({ isOpen, onClose, onConfirm, isSubmitting }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-anchor-card border border-white/[0.08] rounded-xl p-6 w-full max-w-sm mx-4 shadow-2xl">
        <h3 className="text-lg font-medium text-white mb-2">Deploy Updates</h3>
        <p className="text-sm text-anchor-muted mb-5">
          This will set the latest agent release to 100% rollout. All agents will receive the update on their next check-in.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-2.5 border border-white/[0.08] rounded-lg text-sm font-medium text-anchor-light bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isSubmitting}
            className="flex-1 flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-anchor-blue hover:bg-anchor-blue/80 transition-colors disabled:opacity-50"
          >
            {isSubmitting && <ButtonSpinner />}
            {isSubmitting ? 'Deploying...' : 'Confirm Deploy'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Quick Actions
const QuickActions: React.FC<{
  onNavigate: (path: string) => void;
  tenantId?: string;
  onDataRefresh?: () => void;
}> = ({ onNavigate: _onNavigate, tenantId, onDataRefresh }) => {
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showDeployConfirm, setShowDeployConfirm] = useState(false);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    setToast({ message, type });
  }, []);

  const handleRunScan = async () => {
    setLoadingAction('scan');
    try {
      const result = await api.triggerFleetScan();
      const count = result?.agentsTriggered ?? 0;
      showToast(`Scan triggered on ${count} agent(s) — results will appear shortly`, 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Scan failed: ${msg}`, 'error');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDeployUpdates = async () => {
    setLoadingAction('deploy');
    try {
      const releases = await api.getAgentReleases();
      const latest = Array.isArray(releases) ? releases[0] : null;
      if (!latest) {
        showToast('No updates available — publish a release first', 'info');
        setLoadingAction(null);
        return;
      }
      if (!latest.is_active) {
        showToast('No active release available to deploy', 'info');
        setLoadingAction(null);
        return;
      }
      setShowDeployConfirm(true);
      setLoadingAction(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Failed to check releases: ${msg}`, 'error');
      setLoadingAction(null);
    }
  };

  const confirmDeploy = async () => {
    setLoadingAction('deploy');
    try {
      await api.triggerUpdateRollout();
      showToast('Update rollout set to 100% — agents will update on next check-in', 'success');
      setShowDeployConfirm(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Deploy failed: ${msg}`, 'error');
    } finally {
      setLoadingAction(null);
    }
  };

  const handlePostureReport = async () => {
    setLoadingAction('report');
    try {
      await api.downloadPostureReportCsv();
      showToast('Posture report downloaded', 'success');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Export failed: ${msg}`, 'error');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleAddClient = async (name: string, contactEmail: string) => {
    if (!tenantId) {
      showToast('Tenant ID not available', 'error');
      return;
    }
    setLoadingAction('addClient');
    try {
      await api.createClient(tenantId, { name, contactEmail: contactEmail || undefined });
      showToast(`Client "${name}" created successfully`, 'success');
      setShowAddClient(false);
      onDataRefresh?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Failed to create client: ${msg}`, 'error');
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <>
      <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-6">
        <h3 className="text-lg font-medium text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleDeployUpdates}
            disabled={loadingAction !== null}
            className="flex items-center justify-center px-4 py-3 border border-white/[0.08] rounded-lg text-sm font-medium text-anchor-light bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingAction === 'deploy' && <ButtonSpinner />}
            Deploy Updates
          </button>
          <button
            onClick={handleRunScan}
            disabled={loadingAction !== null}
            className="flex items-center justify-center px-4 py-3 border border-white/[0.08] rounded-lg text-sm font-medium text-anchor-light bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingAction === 'scan' && <ButtonSpinner />}
            Run Security Scan
          </button>
          <button
            onClick={handlePostureReport}
            disabled={loadingAction !== null}
            className="flex items-center justify-center px-4 py-3 border border-white/[0.08] rounded-lg text-sm font-medium text-anchor-light bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loadingAction === 'report' && <ButtonSpinner />}
            Posture Report
          </button>
          <button
            onClick={() => setShowAddClient(true)}
            disabled={loadingAction !== null}
            className="flex items-center justify-center px-4 py-3 border border-white/[0.08] rounded-lg text-sm font-medium text-anchor-light bg-white/[0.02] hover:bg-white/[0.06] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Client
          </button>
        </div>
        {toast && (
          <ActionToast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />
        )}
      </div>
      <AddClientModal
        isOpen={showAddClient}
        onClose={() => setShowAddClient(false)}
        onSubmit={handleAddClient}
        isSubmitting={loadingAction === 'addClient'}
      />
      <DeployConfirmDialog
        isOpen={showDeployConfirm}
        onClose={() => setShowDeployConfirm(false)}
        onConfirm={confirmDeploy}
        isSubmitting={loadingAction === 'deploy'}
      />
    </>
  );
};

// Helper: convert module_scores JSONB into ModuleStatus[] for OrgMosaicGrid
function moduleScoresToModuleStatus(
  agentScores: any[]
): Array<{ name: string; status: 'pass' | 'warn' | 'fail' | 'gap'; score: number }> {
  // Merge module_scores across all agents for this client (average per module)
  const moduleMap: Record<string, { totalScore: number; count: number; totalFindings: number }> = {};

  for (const row of agentScores) {
    const ms = row.module_scores || row.moduleScores;
    if (!ms || typeof ms !== 'object') continue;
    for (const [key, val] of Object.entries(ms) as [string, any][]) {
      if (!moduleMap[key]) moduleMap[key] = { totalScore: 0, count: 0, totalFindings: 0 };
      moduleMap[key].totalScore += Number(val.score || 0);
      moduleMap[key].count += 1;
      moduleMap[key].totalFindings += Number(val.findings || 0);
    }
  }

  const st = (v: number): 'pass' | 'warn' | 'fail' => v > 90 ? 'pass' : v > 70 ? 'warn' : 'fail';

  return Object.entries(moduleMap).map(([key, data]) => {
    const avgScore = Math.round(data.totalScore / data.count);
    // Format the module name: replace underscores with spaces, title case
    const name = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return { name, status: st(avgScore), score: avgScore };
  });
}

// Main Dashboard
const AuthenticatedDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [clientModuleScores, setClientModuleScores] = useState<
    Record<string, Array<{ name: string; status: 'pass' | 'warn' | 'fail' | 'gap'; score: number }>>
  >({});

  const fetchData = useCallback(async () => {
    try {
      // Fetch compliance summary, agent stats, and alerts in parallel
      const [summary, agentStats, alertsData] = await Promise.all([
        api.getComplianceSummary(),
        api.getAgentStats().catch(() => null),
        api.getAlerts().catch(() => []),
      ]);

      const clients = (summary.clients || []).map((c: any) => ({
        ...c,
        industry: '',
      }));

      setData({
        tenant: { name: summary.tenantName || user?.name || 'Data Anchor', subscription: 'Enterprise' },
        clients,
        fleetStats: {
          totalAgents: summary.totalAgents || 0,
          online: summary.onlineAgents || 0,
          offline: (summary.totalAgents || 0) - (summary.onlineAgents || 0),
          error: agentStats?.error ?? 0,
          maintenance: agentStats?.maintenance ?? 0,
        },
        avgScore: summary.avgScore || 0,
        alerts: Array.isArray(alertsData) ? alertsData : [],
      });
      setError(null);

      // Fetch per-client module scores in parallel (non-blocking — dashboard renders without them)
      const clientIds = clients.map((c: any) => c.clientId).filter(Boolean);
      if (clientIds.length > 0) {
        const scoreResults = await Promise.allSettled(
          clientIds.map((id: string) => api.getClientScores(id))
        );
        const moduleMap: Record<string, Array<{ name: string; status: 'pass' | 'warn' | 'fail' | 'gap'; score: number }>> = {};
        scoreResults.forEach((result, idx) => {
          if (result.status === 'fulfilled' && Array.isArray(result.value) && result.value.length > 0) {
            moduleMap[clientIds[idx]] = moduleScoresToModuleStatus(result.value);
          }
        });
        setClientModuleScores(moduleMap);
      }
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      setError(`Unable to load dashboard data — ${errMsg}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000); // Auto-refresh every 60s

    // Try to connect WebSocket for real-time updates (non-blocking)
    const unsubscribes: Array<() => void> = [];
    try {
      connectSocket(apiConfig.baseUrl);
      unsubscribes.push(onEvent('agent:status-change', () => fetchData()));
      unsubscribes.push(onEvent('audit:complete', () => fetchData()));
    } catch {
      // WebSocket not available — polling fallback is already running
    }

    return () => {
      clearInterval(interval);
      unsubscribes.forEach((unsub) => unsub());
      disconnectSocket();
    };
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-anchor-blue mx-auto"></div>
          <p className="mt-4 text-anchor-muted">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-medium text-white">{user?.name || 'Dashboard'}</h2>
          <p className="text-anchor-muted mt-1">Security posture overview</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-8 text-center">
          <div className="text-red-400 text-sm mb-3">{error || 'Unable to load dashboard data'}</div>
          <button
            onClick={() => { setLoading(true); setError(null); fetchData(); }}
            className="px-4 py-2 bg-anchor-blue/20 border border-anchor-blue/40 rounded-lg text-sm text-anchor-blue hover:bg-anchor-blue/30 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const lowestClient = [...data.clients].sort((a, b) => a.avgScore - b.avgScore)[0];

  return (
    <div className="space-y-8">
      {/* API status banner */}
      {error && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-4 py-2 text-xs text-amber-300 flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          {error}
        </div>
      )}

      {/* Page header */}
      <div>
        <h2 className="text-2xl font-medium text-white">{user?.name || data.tenant.name}</h2>
        <p className="text-anchor-muted mt-1">
          {data.fleetStats.totalAgents} Agents · {data.clients.length} Clients
        </p>
      </div>

      {/* Trust Score Hero */}
      <TrustScoreCard
        title="Fleet Posture"
        subtitle={`${data.clients.length} ${data.clients.length === 1 ? 'client' : 'clients'}`}
        score={data.avgScore}
        agentCount={data.fleetStats.totalAgents}
        onlineCount={data.fleetStats.online}
      />

      {/* Per-client trust scores */}
      {data.clients.length > 0 && (
        <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-3">Client Posture</h3>
          <div className="space-y-1">
            {data.clients.map((client) => (
              <div
                key={client.clientId}
                onClick={() => navigate(`/clients/${client.clientId}`)}
                className="cursor-pointer rounded-lg transition-colors hover:bg-white/[0.03]"
                role="link"
                aria-label={`Open ${client.clientName} posture triage`}
              >
                <TrustScoreCard
                  title={client.clientName}
                  score={client.avgScore}
                  agentCount={client.agentCount}
                  onlineCount={client.onlineCount}
                  compact
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          <FleetOverviewCard stats={data.fleetStats} score={data.avgScore} />
          <ClientsTable clients={data.clients} onClientClick={(clientId) => navigate(`/clients/${clientId}`)} />
        </div>

        {/* Right sidebar */}
        <div className="space-y-8">
          {lowestClient && lowestClient.avgScore < 95 && (
            <CriticalPathCard clientName={lowestClient.clientName} currentScore={lowestClient.avgScore} />
          )}

          {data.clients.length > 0 && (
            <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-5">
              <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider mb-4">Anchor Compliance</h3>
              <div className="grid grid-cols-2 gap-4">
                {data.clients.slice(0, 4).map((client) => {
                  const modules = clientModuleScores[client.clientId] || [];
                  return (
                    <div key={client.clientId} className="flex flex-col items-center">
                      <OrgMosaicGrid orgName={client.clientName} modules={modules} size={100} showLabels />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {lowestClient && (
            <VerifyRemediation
              clientName={lowestClient.clientName}
              currentScore={lowestClient.avgScore}
              onVerify={async () => {
                const result = await api.verifyRemediation(lowestClient.clientId);
                // Map API response to component's RemediationResult shape
                const statusFromScore = (s: number): 'pass' | 'warn' | 'fail' =>
                  s >= 90 ? 'pass' : s >= 70 ? 'warn' : 'fail';
                return {
                  beforeScore: result.previousScore,
                  afterScore: result.currentScore,
                  changes: (result.changes || []).map((c: any) => ({
                    name: c.module,
                    before: statusFromScore(c.before),
                    after: statusFromScore(c.after),
                    endpointCount: undefined,
                  })),
                  modulesPassingBefore: result.modulesPassingBefore,
                  modulesPassingAfter: result.modulesPassingAfter,
                };
              }}
            />
          )}

          {/* Alerts */}
          {data.alerts.length > 0 && (
            <div className="bg-anchor-card border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-white/50 uppercase tracking-wider">Alerts</h3>
                <span className="text-xs text-white/30">{data.alerts.length}</span>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {data.alerts.map((alert) => {
                  const severityStyles: Record<string, string> = {
                    critical: 'border-red-500/30 bg-red-500/5',
                    warning: 'border-amber-500/30 bg-amber-500/5',
                    info: 'border-anchor-blue/30 bg-anchor-blue/5',
                  };
                  const dotStyles: Record<string, string> = {
                    critical: 'bg-red-400',
                    warning: 'bg-amber-400',
                    info: 'bg-anchor-blue',
                  };
                  return (
                    <div
                      key={alert.id}
                      className={`border rounded-lg px-3 py-2 ${severityStyles[alert.severity] || severityStyles.info}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${dotStyles[alert.severity] || dotStyles.info}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-white/70 leading-relaxed">{alert.message}</p>
                          <p className="text-[10px] text-white/30 mt-1">
                            {new Date(alert.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              await api.dismissAlert(alert.id);
                              setData((prev) =>
                                prev ? { ...prev, alerts: prev.alerts.filter((a) => a.id !== alert.id) } : prev
                              );
                            } catch { /* dismiss failed — alert stays visible */ }
                          }}
                          className="text-white/20 hover:text-white/50 transition-colors flex-shrink-0 ml-1"
                          title="Dismiss"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <QuickActions onNavigate={navigate} tenantId={user?.tenantId} onDataRefresh={fetchData} />
        </div>
      </div>
    </div>
  );
};

export default AuthenticatedDashboard;
